import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCard,
  PrismDataBar,
  PrismIcon,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { t, tKey } from 'ember-intl';
import HotelStrategySubtypeIcon from 'lumen/components/hotel/strategy/subtype-icon';
import HotelStrategySubtypeMetric from 'lumen/components/hotel/strategy/subtype-metric';
import HotelStrategyTooltipsSubtypeTooltipContent from 'lumen/components/hotel/strategy/tooltips/subtype-tooltip-content';
import {
  SUBTYPE_VALUES_KEY_CANCELLATION_POLICY,
  SUBTYPE_VALUES_KEY_STRATEGY,
} from 'lumen/utils/constants/rate-strategy-constants';
import {
  formatCancellationpolicyValue,
  formatStrategyValue,
} from 'lumen/utils/strategy/format-helper-utils';

interface HotelStrategySubtypeMetricBlockSignature {
  Args: {
    dataToShow: unknown;
    subtype: unknown;
    switchToDetailViaPositionButton: unknown;
  };
}

export default class HotelStrategySubtypeMetricBlock extends Component<HotelStrategySubtypeMetricBlockSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare rsSelections: Services['rs-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedCompsetMedian() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.subtype.formattedCompsetMedians[this.args.dataToShow];
  }

  @computed('rsEntity.formattedValue')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.rsEntity?.formattedValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasChangeOverTime() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      !this.rsEntity.hasNoDataForLatestOrHistoricReference &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.rsEntity.formattedChangeOverTime
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasCompsetChangeOverTime() {
    if (
      // @ts-expect-error: Incorrect type
      this.args.subtype.hasStrategies &&
      this.args.dataToShow === SUBTYPE_VALUES_KEY_STRATEGY
    ) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const areStrategiesWithChanges = this.args.subtype.filteredStrategies
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .filter((strategy) => !strategy.hasNoData && !strategy.isOwnHotel)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        .some((strategy) => strategy.changeOverTime);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return areStrategiesWithChanges;
    }

    // @ts-expect-error: Incorrect type
    if (this.args.subtype.hasCancellationPolicies) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const areCancellationsWithChanges =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.subtype.filteredCancellationpolicies
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .filter(
            // @ts-expect-error: Incorrect type
            (cancellationpolicy) =>
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              !cancellationpolicy.hasNoData && !cancellationpolicy.isOwnHotel,
          )
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          .some((cancellationpolicy) => cancellationpolicy.changeOverTime);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return areCancellationsWithChanges;
    }

    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNonComputableDataOnly() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.subtype.hasNonComputableDataOnly[this.args.dataToShow];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAbsolute() {
    // Cancellation policies and mealtype strategies (supplement) have absolute numbers
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      this.args.dataToShow === SUBTYPE_VALUES_KEY_CANCELLATION_POLICY ||
      // @ts-expect-error: Incorrect type
      this.args.subtype.isMealtypeType
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOwnStrategyNonComputable() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.subtype.ownHotelData[this.args.dataToShow]) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return this.args.subtype.ownHotelData[this.args.dataToShow]
        .isNonComputable;
    }

    return false;
  }

  // Note: will probably need to add handling missing data later (+ same data in tooltips)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get progressBarValues() {
    if (this.args.dataToShow === SUBTYPE_VALUES_KEY_STRATEGY) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return (
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.subtype.filteredStrategies
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .filter((strategy) => !strategy.hasNoData) // Don't take missing/soldout/non-computable/not-offered strategies into account for the median calculation
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .map((strategy) => ({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            value: strategy.supplementOrDiscount,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            isMainPoint: strategy.isOwnHotel,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            isAbsolute: strategy.isAbsolute,
          }))
      );
    }

    if (this.args.dataToShow === SUBTYPE_VALUES_KEY_CANCELLATION_POLICY) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return (
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.subtype.filteredCancellationpolicies
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .filter((cancellationPolicy) => !cancellationPolicy.hasNoData) // Don't take missing/soldout/non-computable/not-offered cancellation policies into account for the median calculation
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .map((cancellationPolicy) => ({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            value: cancellationPolicy.days,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            isMainPoint: cancellationPolicy.isOwnHotel,
          }))
      );
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rsEntity() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.subtype.ownHotelData[this.args.dataToShow];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showSubtypePositionButton() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      this.args.dataToShow === SUBTYPE_VALUES_KEY_STRATEGY &&
      // @ts-expect-error: Incorrect type
      this.args.subtype.hasStrategySubtypePosition
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get strategyLabel() {
    let strategyLabelKey = tKey('strategy.metric_block.your_discount');

    if (this.args.dataToShow === SUBTYPE_VALUES_KEY_CANCELLATION_POLICY) {
      // @ts-expect-error: Incorrect type
      strategyLabelKey = tKey('strategy.metric_block.your_policy');
      // @ts-expect-error: Incorrect type
    } else if (this.args.subtype.isMealtypeType) {
      // @ts-expect-error: Incorrect type
      strategyLabelKey = tKey('strategy.metric_block.your_supplement');
      // @ts-expect-error: Incorrect type
    } else if (this.args.subtype.isPosType) {
      // @ts-expect-error: Incorrect type
      strategyLabelKey = tKey('strategy.metric_block.your_median_rate');
    }

    return this.intl.t(strategyLabelKey).toString();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subTypeHeaderLabel() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.subtype.formattedLabel[this.args.dataToShow];
  }

  @action
  onPositionButtonClicked(): void {
    // @ts-expect-error: Incorrect type
    this.args.switchToDetailViaPositionButton(this.args.subtype);
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  plottedPointsValueFormatter(value) {
    if (this.args.dataToShow === SUBTYPE_VALUES_KEY_STRATEGY) {
      return formatStrategyValue(
        this.intl,
        this.hotelAndMarketService,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        value,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.isAbsolute,
      );
    }

    if (this.args?.dataToShow === SUBTYPE_VALUES_KEY_CANCELLATION_POLICY) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return formatCancellationpolicyValue(this.intl, value);
    }

    return '';
  }

  <template>
    {{! @glint-nocheck }}
    <PrismCard @isSecondary={{true}} class="rate-strategy-subtype-metric-block">
      <:header as |Header|>
        <div class="flex items-center gap-200 truncate">
          {{! subtype-header.hbs logic needs to replicated for EmberTooltip }}
          <HotelStrategySubtypeIcon
            @isBranddotcomMemberRatesSubType={{@subtype.isBranddotcomMemberRatesSubType}}
            @isMealtypeType={{@subtype.isMealtypeType}}
            @isMemberRatesType={{@subtype.isMemberRatesType}}
            @isMobileBookingSubtype={{@subtype.isMobileBookingSubtype}}
            @isMobileExpediaSubtype={{@subtype.isMobileExpediaSubtype}}
            @isPosBookingSubtype={{@subtype.isPosBookingSubtype}}
            @isPosExpediaSubtype={{@subtype.isPosExpediaSubtype}}
            @label={{@subtype.label}}
          />
          {{#unless @subtype.isMemberRatesType}}
            <p class="text-300--bold truncate">
              <Header.title>{{this.subTypeHeaderLabel}}</Header.title>
              <PrismTooltip @text={{this.subTypeHeaderLabel}} />
            </p>
          {{/unless}}
        </div>
      </:header>

      <:headerAfter>
        {{#if this.showSubtypePositionButton}}
          {{#if @subtype.hasNeutralPosition}}
            <button
              class="button--unstyled position-badge"
              data-event-id="hotel-rate-strategy-card-subtype-position-button-clicked"
              data-event-value={{@subtype.id}}
              slot="header-after"
              type="button"
              {{on "click" this.onPositionButtonClicked}}
            >
              {{t
                "strategy.metric_block.your_position"
                position=@subtype.formattedSubtypePosition
              }}
            </button>
          {{else}}
            <button
              class="button--unstyled position-badge position-badge--warning"
              data-event-id="hotel-rate-strategy-card-subtype-position-button-clicked"
              data-event-value={{@subtype.id}}
              slot="header-after"
              type="button"
              {{on "click" this.onPositionButtonClicked}}
            >
              {{t
                "strategy.metric_block.your_position"
                position=@subtype.formattedSubtypePosition
              }}
              <PrismIcon
                @name={{if
                  @subtype.isHigherThanUsualPricePoint
                  "arrow-down"
                  "arrow-up"
                }}
                @size="small"
                class="ml-100"
              />
            </button>
          {{/if}}
        {{/if}}
      </:headerAfter>

      <:content>
        <div
          class="rate-strategy-subtype-metric-block__metric-wrapper flex flex-col"
        >
          <div class="flex flex-row gap-100">
            <HotelStrategySubtypeMetric
              @changeOverTime={{this.hasChangeOverTime}}
              @isOwnHotel={{true}}
              @label={{this.strategyLabel}}
              @showNonComputableWarning={{this.isOwnStrategyNonComputable}}
              @subtype={{@subtype}}
              @value={{this.formattedValue}}
            />
            <HotelStrategySubtypeMetric
              @changeOverTime={{this.hasCompsetChangeOverTime}}
              @isOwnHotel={{false}}
              @label={{this.rsSelections.compsetString}}
              @showNonComputableWarning={{this.hasNonComputableDataOnly}}
              @subtype={{@subtype}}
              @value={{this.formattedCompsetMedian}}
            />
          </div>

          <PrismDataBar
            @data={{this.progressBarValues}}
            @isAbsolute={{this.isAbsolute}}
            @type="plotted-points"
            @valueFormatter={{this.plottedPointsValueFormatter}}
          />

          {{#if this.progressBarValues.length}}
            <EmberTooltip
              @tooltipClass="default-tooltip default-tooltip--no-max-width default-table-tooltip rate-strategy-subtype-tooltip"
            >
              <HotelStrategyTooltipsSubtypeTooltipContent
                @dataToShow={{@dataToShow}}
                @subTypeHeaderLabel={{this.subTypeHeaderLabel}}
                @subtype={{@subtype}}
              />
            </EmberTooltip>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
